/**
 * useMarkAllNotificationsRead — mutation hook for FR-005 (bulk markAllRead).
 *
 * Wraps `api.notifications.markAllRead` with an auto-re-issue loop capped
 * at 10 iterations. On overflow, the hook transitions to `error` with the
 * exact message from specs/prd.md §3 FR-005.
 *
 * Mount-safety: the hook owns an `isMountedRef` checked before each
 * iteration and before every state update (Finding C-13).
 *
 * Reference stability: both `action` (via `useCallback`) and the return
 * object (via `useMemo`) are stable across renders (Finding C-2).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import type { NotificationMutationState } from "../types/notification";

/** Maximum number of auto-re-issue iterations before the loop guard fires. */
const MAX_LOOP_ITERATIONS = 10;

/** Exact error message mandated by specs/prd.md §3 FR-005. */
const LOOP_OVERFLOW_MESSAGE =
  "Mark all read loop exceeded 10 iterations — server may not be persisting read state.";

/** Result shape from the markAllRead mutation. */
interface MarkAllReadResult {
  ok: true;
  count: number;
  hasMore: boolean;
}

/**
 * Named hook wrapping `api.notifications.markAllRead` with auto-re-issue loop.
 *
 * @returns Flat `NotificationMutationState<void, MarkAllReadResult>` with
 *   stable `action` reference across renders.
 */
export function useMarkAllNotificationsRead(): NotificationMutationState<
  void,
  MarkAllReadResult
> {
  const mutate = useMutation(api.notifications.markAllRead);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [isMarking, setIsMarking] = useState(false);
  const [status, setStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [lastResult, setLastResult] = useState<MarkAllReadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const action = useCallback(() => {
    if (!isMountedRef.current) return;

    setIsMarking(true);
    setStatus("pending");
    setError(null);

    const loop = async () => {
      let iteration = 0;

      while (iteration < MAX_LOOP_ITERATIONS) {
        if (!isMountedRef.current) return;

        iteration++;

        try {
          const result = (await mutate()) as MarkAllReadResult;

          if (!isMountedRef.current) return;

          if (!result.hasMore) {
            // Terminal success — flush synchronously so state is
            // immediately observable in test assertions.
            flushSync(() => {
              setIsMarking(false);
              setStatus("success");
              setLastResult(result);
            });
            return;
          }

          // hasMore is true — continue the loop
          if (!isMountedRef.current) return;
          setLastResult(result);
        } catch (err: unknown) {
          if (!isMountedRef.current) return;

          const message =
            err instanceof Error ? err.message : String(err);
          flushSync(() => {
            setIsMarking(false);
            setStatus("error");
            setError(message);
          });
          if (!(err instanceof Error && "data" in err)) {
            console.error(err);
          }
          return;
        }
      }

      // Loop guard: exceeded MAX_LOOP_ITERATIONS with hasMore still true
      if (!isMountedRef.current) return;

      console.error(
        JSON.stringify({
          event: "notifications.markAllRead.loopGuard",
          level: "ERROR",
          iterations: MAX_LOOP_ITERATIONS,
          ts_ms: Date.now(),
        }),
      );

      flushSync(() => {
        setIsMarking(false);
        setStatus("error");
        setError(LOOP_OVERFLOW_MESSAGE);
      });
    };

    void loop();
  }, [mutate]);

  return useMemo(
    () => ({ action, isMarking, status, lastResult, error }),
    [action, isMarking, status, lastResult, error],
  );
}
